import React from "react";
import { api } from "../../lib/api";
import { CarouselEditor } from "../../components/marketing/CarouselEditor";
import { ContentPlanner } from "../../components/marketing/ContentPlanner";
import {
  Sparkles, Download, Image as ImageIcon, RefreshCw, Check, Copy,
  Trash2, Upload, Loader2, FileArchive, ChevronDown, ChevronUp,
  AlertCircle, Pencil, X,
} from "lucide-react";
const CATEGORY_LABELS = {
  coulisses: "Coulisses / équipe",
  avant_apres: "Avant / Après retouche",
  astuce_setup: "Astuce setup lumière",
  offre_cta: "Offre / CTA réservation",
  portfolio: "Portfolio",
  other: "Autre",
};

const CATEGORY_COLORS = {
  coulisses: "bg-amber-500/15 text-amber-200 border-amber-500/30",
  avant_apres: "bg-emerald-500/15 text-emerald-200 border-emerald-500/30",
  astuce_setup: "bg-sky-500/15 text-sky-200 border-sky-500/30",
  offre_cta: "bg-rose-500/15 text-rose-200 border-rose-500/30",
  portfolio: "bg-violet-500/15 text-violet-200 border-violet-500/30",
  other: "bg-white/10 text-white/70 border-white/20",
};

const BACKEND = process.env.REACT_APP_BACKEND_URL;

function absUrl(u) {
  if (!u) return "";
  if (u.startsWith("http")) return u;
  return `${BACKEND}${u}`;
}

function Badge({ children, tone = "default", testid }) {
  const tones = {
    default: "bg-white/10 text-white/70 border-white/20",
    warn: "bg-amber-500/15 text-amber-200 border-amber-500/30",
    ok: "bg-emerald-500/15 text-emerald-200 border-emerald-500/30",
    ai: "bg-sky-500/15 text-sky-200 border-sky-500/30",
    photo: "bg-amber-500/15 text-amber-200 border-amber-500/30",
  };
  return (
    <span data-testid={testid}
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-xs font-light ${tones[tone] || tones.default}`}>
      {children}
    </span>
  );
}

// ============================================================
// MEDIA LIBRARY TAB
// ============================================================
const CATEGORY_HELP = {
  astuce_setup: "Setups lumière, softbox, flashs, trépieds — utilisés pour les posts \"Astuce\"",
  avant_apres: "Avant / Après retouche — utilisés pour les posts \"Retouche\"",
  coulisses: "Vous en action, préparation, équipe — utilisés pour les Stories coulisses",
  offre_cta: "Visuels d'offre, séances, réservation — utilisés pour les posts \"CTA\"",
  portfolio: "Beaux portraits finaux — utilisés comme repli si une autre catégorie est vide",
  other: "Non classé (ne sera pas utilisé automatiquement)",
};

function CategorySection({ title, categoryKey, items, onUpload, uploading, onUpdate, onDelete, onGenerateAi, generatingAi }) {
  const inputRef = React.useRef(null);
  const [aiCount, setAiCount] = React.useState(3);
  const supportsAi = ["astuce_setup", "offre_cta", "coulisses", "avant_apres", "portfolio"].includes(categoryKey);
  return (
    <div className="rounded-xl border border-white/10 bg-black/15" data-testid={`media-section-${categoryKey}`}>
      <div className="flex items-start justify-between gap-3 p-4 border-b border-white/5 flex-wrap">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h4 className="font-serif text-lg">{title}</h4>
            <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-xs font-light ${CATEGORY_COLORS[categoryKey] || CATEGORY_COLORS.other}`}>
              {items.length} photo{items.length > 1 ? "s" : ""}
            </span>
          </div>
          <p className="text-xs text-text-cream/50 mt-1 font-light">{CATEGORY_HELP[categoryKey]}</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap justify-end">
          {supportsAi && (
            <div className="flex items-center gap-1">
              <select
                value={aiCount}
                onChange={(e) => setAiCount(Number(e.target.value))}
                data-testid={`media-ai-count-${categoryKey}`}
                className="bg-black/40 border border-white/10 rounded px-2 py-1.5 text-xs"
              >
                {[1,2,3,4,5,6,8,10].map(n => <option key={n} value={n}>{n}</option>)}
              </select>
              <button
                className="btn btn-outline text-xs"
                onClick={() => onGenerateAi(categoryKey, aiCount)}
                disabled={generatingAi === categoryKey}
                data-testid={`media-ai-generate-${categoryKey}`}
                title="Générer des photos IA variées via Nano Banana"
              >
                {generatingAi === categoryKey
                  ? <><Loader2 className="animate-spin" size={14}/> IA…</>
                  : <><Sparkles size={14}/> Générer IA</>}
              </button>
            </div>
          )}
          <button
            className="btn btn-primary text-xs"
            onClick={() => inputRef.current?.click()}
            disabled={uploading === categoryKey}
            data-testid={`media-upload-${categoryKey}`}
          >
            {uploading === categoryKey
              ? <><Loader2 className="animate-spin" size={14}/> Upload…</>
              : <><Upload size={14}/> Importer</>}
          </button>
        </div>
        <input
          type="file" accept="image/*" multiple hidden ref={inputRef}
          onChange={(e) => { onUpload(categoryKey, e.target.files); e.target.value = ""; }}
        />
      </div>

      {items.length === 0 ? (
        <div
          className="p-8 text-center text-text-cream/40 text-sm font-light cursor-pointer hover:bg-white/5 transition"
          onClick={() => inputRef.current?.click()}
          data-testid={`media-empty-${categoryKey}`}
        >
          Aucune photo dans cette catégorie — cliquez pour importer
        </div>
      ) : (
        <div className="p-4 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3">
          {items.map((it) => (
            <div
              key={it.id}
              data-testid={`media-card-${it.id}`}
              className="rounded-lg overflow-hidden border border-white/10 bg-black/30 flex flex-col group"
            >
              <div className="relative aspect-[4/5] bg-black/40 overflow-hidden">
                <img src={absUrl(it.url)} alt={it.label} className="w-full h-full object-cover"/>
                <button
                  onClick={() => onDelete(it.id)}
                  data-testid={`media-delete-${it.id}`}
                  className="absolute top-2 right-2 p-1.5 rounded-full bg-black/60 hover:bg-red-500/70 transition opacity-0 group-hover:opacity-100"
                >
                  <Trash2 size={14}/>
                </button>
                {!it.usable_for_marketing && (
                  <div className="absolute inset-0 bg-black/60 flex items-center justify-center text-xs text-text-cream/60">
                    Désactivée
                  </div>
                )}
              </div>
              <div className="p-2 flex flex-col gap-1.5 text-xs">
                <select
                  value={it.category}
                  onChange={(e) => onUpdate(it.id, { category: e.target.value })}
                  data-testid={`media-category-${it.id}`}
                  className="w-full bg-black/40 border border-white/10 rounded px-1.5 py-1 text-xs"
                >
                  {Object.entries(CATEGORY_LABELS).map(([k, l]) => (
                    <option key={k} value={k}>{l}</option>
                  ))}
                </select>
                <label className="flex items-center gap-1.5 cursor-pointer select-none text-text-cream/70">
                  <input
                    type="checkbox"
                    checked={it.usable_for_marketing}
                    onChange={(e) => onUpdate(it.id, { usable_for_marketing: e.target.checked })}
                    data-testid={`media-usable-${it.id}`}
                  />
                  Utilisable IG
                </label>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function MediaLibrary() {
  const [items, setItems] = React.useState([]);
  const [uploading, setUploading] = React.useState("");
  const [generatingAi, setGeneratingAi] = React.useState("");
  const [err, setErr] = React.useState("");

  const load = React.useCallback(async () => {
    try {
      const r = await api.get("/admin/marketing/media");
      setItems(r.data.items || []);
    } catch { /* ignore */ }
  }, []);

  React.useEffect(() => { load(); }, [load]);

  const upload = async (category, files) => {
    if (!files || !files.length) return;
    setUploading(category); setErr("");
    try {
      const token = localStorage.getItem("se_admin_token");
      let failures = 0;
      for (const f of files) {
        const fd = new FormData();
        fd.append("file", f);
        const r = await fetch(`${BACKEND}/api/admin/upload`, {
          method: "POST", body: fd, headers: { Authorization: `Bearer ${token}` },
        });
        if (!r.ok) { failures++; continue; }
        const data = await r.json();
        await api.post("/admin/marketing/media", {
          url: data.url,
          label: f.name.replace(/\.[^.]+$/, ""),
          category,
          usable_for_marketing: true,
          width: data.width || 0,
          height: data.height || 0,
        });
      }
      if (failures) setErr(`${failures} fichier(s) ont échoué (format non supporté ou > 100 MB)`);
      await load();
    } catch (e) {
      setErr(e?.response?.data?.detail || "Erreur upload");
    } finally {
      setUploading("");
    }
  };

  const generateAi = async (category, count) => {
    setGeneratingAi(category); setErr("");
    try {
      const r = await api.post("/admin/marketing/media/generate-ai", { category, count });
      await load();
      if (r.data.failed > 0) setErr(`${r.data.failed} génération(s) IA ont échoué (ressayez)`);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Erreur génération IA");
    } finally {
      setGeneratingAi("");
    }
  };

  const update = async (id, patch) => {
    await api.patch(`/admin/marketing/media/${id}`, patch);
    load();
  };

  const remove = async (id) => {
    if (!confirm("Supprimer cette photo de la bibliothèque ?")) return;
    await api.delete(`/admin/marketing/media/${id}`);
    load();
  };

  const categoryOrder = ["astuce_setup", "avant_apres", "coulisses", "offre_cta", "portfolio", "other"];

  const byCategory = Object.fromEntries(
    categoryOrder.map(k => [k, items.filter(it => it.category === k)])
  );

  return (
    <div data-testid="marketing-media-tab">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h3 className="font-serif text-2xl">Bibliothèque de photos studio</h3>
          <p className="text-sm text-text-cream/50 mt-2 max-w-3xl font-light">
            Importez vos photos directement dans la bonne catégorie — un bouton d'import par section.
            Elles seront piochées automatiquement lors de la génération des kits Instagram.
          </p>
        </div>
        <div className="text-sm text-text-cream/50">
          Total : <strong className="text-text-cream/80">{items.length}</strong> photo{items.length > 1 ? "s" : ""}
        </div>
      </div>

      {err && (
        <div className="mt-3 p-3 rounded-lg bg-red-500/10 border border-red-500/30 text-red-200 text-sm flex items-center gap-2">
          <AlertCircle size={16}/> {err}
        </div>
      )}

      <div className="mt-6 flex flex-col gap-4">
        {categoryOrder.map((k) => (
          <CategorySection
            key={k}
            title={CATEGORY_LABELS[k]}
            categoryKey={k}
            items={byCategory[k]}
            onUpload={upload}
            uploading={uploading}
            onUpdate={update}
            onDelete={remove}
            onGenerateAi={generateAi}
            generatingAi={generatingAi}
          />
        ))}
      </div>
    </div>
  );
}

// ============================================================
// KIT ITEM CARD
// ============================================================
function KitItemCard({ item, onPatch, onRegenText, onRegenImage, kind }) {
  const [working, setWorking] = React.useState("");
  const [draft, setDraft] = React.useState({
    title: item.title,
    caption: item.caption,
    hashtags: (item.hashtags || []).join(" "),
    overlay_lines: (item.overlay_lines || []).join("\n"),
  });

  React.useEffect(() => {
    setDraft({
      title: item.title,
      caption: item.caption,
      hashtags: (item.hashtags || []).join(" "),
      overlay_lines: (item.overlay_lines || []).join("\n"),
    });
  }, [item.id, item.title, item.caption, item.hashtags, item.overlay_lines]);

  const save = async () => {
    setWorking("save");
    try {
      await onPatch(item.id, {
        title: draft.title,
        caption: draft.caption,
        hashtags: draft.hashtags.split(/\s+/).filter(h => h.startsWith("#")),
        overlay_lines: draft.overlay_lines.split(/\n/).map(s => s.trim()).filter(Boolean),
      });
    } finally { setWorking(""); }
  };

  const toggleApprove = async () => {
    setWorking("approve");
    try { await onPatch(item.id, { approved: !item.approved }); } finally { setWorking(""); }
  };

  const togglePublished = async () => {
    setWorking("pub");
    try { await onPatch(item.id, { published: !item.published }); } finally { setWorking(""); }
  };

  const regenText = async () => {
    setWorking("text");
    try { await onRegenText(item.id); } finally { setWorking(""); }
  };

  const regenImage = async () => {
    setWorking("image");
    try { await onRegenImage(item.id); } finally { setWorking(""); }
  };

  const replaceImage = async (file) => {
    if (!file) return;
    setWorking("replace");
    try {
      const token = localStorage.getItem("se_admin_token");
      const fd = new FormData();
      fd.append("file", file);
      const r = await fetch(`${BACKEND}/api/admin/upload`, {
        method: "POST", body: fd, headers: { Authorization: `Bearer ${token}` },
      });
      if (!r.ok) throw new Error("upload");
      const data = await r.json();
      await onPatch(item.id, { image_url: data.url, image_source: "user" });
    } catch (e) {
      alert("Échec remplacement");
    } finally { setWorking(""); }
  };

  const copyAll = async () => {
    const txt = [
      draft.title ? `${draft.title}\n` : "",
      draft.caption,
      "",
      draft.hashtags,
    ].join("\n").trim();
    try { await navigator.clipboard.writeText(txt); } catch { /* ignore */ }
  };

  return (
    <div
      data-testid={`kit-item-${item.id}`}
      className="rounded-xl border border-white/10 bg-black/20 overflow-hidden"
    >
      <div className="grid grid-cols-1 md:grid-cols-[220px_1fr] gap-0">
        {/* Image */}
        <div className={`relative bg-black/40 ${kind === "story" ? "aspect-[9/16]" : "aspect-[4/5]"}`}>
          {item.image_url ? (
            <img src={absUrl(item.image_url)} alt="" className="w-full h-full object-cover"/>
          ) : (
            <div className="w-full h-full flex items-center justify-center text-text-cream/40 text-xs">
              <ImageIcon size={28}/>
            </div>
          )}
          <div className="absolute top-2 left-2 flex gap-1.5 flex-wrap">
            <Badge tone={item.image_source === "ai" ? "ai" : "photo"}>
              {item.image_source === "ai" ? "Image IA" : "Photo studio"}
            </Badge>
          </div>
          {item.published && (
            <div className="absolute top-2 right-2">
              <Badge tone="ok"><Check size={10}/> Publié</Badge>
            </div>
          )}
        </div>

        {/* Content */}
        <div className="p-4 flex flex-col gap-3">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div className="flex items-center gap-2 flex-wrap">
              <h4 className="font-serif text-lg">
                {kind === "post" ? "Post" : "Story"} · {item.day_label} {item.date_label}
              </h4>
              <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-xs font-light ${CATEGORY_COLORS[item.category] || CATEGORY_COLORS.other}`}>
                {CATEGORY_LABELS[item.category] || item.category}
              </span>
              {item.approved && !item.published && <Badge tone="ok">Approuvé</Badge>}
              {!item.approved && <Badge tone="warn">À valider</Badge>}
            </div>
          </div>

          <input
            type="text"
            value={draft.title}
            onChange={(e) => setDraft(s => ({ ...s, title: e.target.value }))}
            placeholder="Titre"
            data-testid={`kit-title-${item.id}`}
            className="bg-black/40 border border-white/10 rounded px-3 py-2 text-sm font-serif"
          />

          <textarea
            value={draft.caption}
            onChange={(e) => setDraft(s => ({ ...s, caption: e.target.value }))}
            rows={kind === "post" ? 6 : 3}
            placeholder="Légende"
            data-testid={`kit-caption-${item.id}`}
            className="bg-black/40 border border-white/10 rounded px-3 py-2 text-sm font-light leading-relaxed resize-none"
          />

          <input
            type="text"
            value={draft.hashtags}
            onChange={(e) => setDraft(s => ({ ...s, hashtags: e.target.value }))}
            placeholder="#hashtags séparés par un espace"
            data-testid={`kit-hashtags-${item.id}`}
            className="bg-black/40 border border-white/10 rounded px-3 py-2 text-xs font-light text-sky-200/80"
          />

          {kind === "story" && (
            <div>
              <label className="text-xs text-text-cream/50 block mb-1">Textes à superposer sur l'image (1 par ligne) :</label>
              <textarea
                value={draft.overlay_lines}
                onChange={(e) => setDraft(s => ({ ...s, overlay_lines: e.target.value }))}
                rows={3}
                placeholder="Astuce du jour&#10;Softbox 90cm&#10;Résultat doux"
                data-testid={`kit-overlays-${item.id}`}
                className="w-full bg-black/40 border border-white/10 rounded px-3 py-2 text-xs font-light resize-none"
              />
            </div>
          )}

          <div className="flex flex-wrap gap-2 pt-2 border-t border-white/5">
            <button
              onClick={save}
              disabled={working !== ""}
              data-testid={`kit-save-${item.id}`}
              className="btn btn-outline text-xs"
            >
              {working === "save" ? <Loader2 className="animate-spin" size={14}/> : <Pencil size={14}/>}
              Enregistrer
            </button>
            <button
              onClick={toggleApprove}
              disabled={working !== ""}
              data-testid={`kit-approve-${item.id}`}
              className={`btn text-xs ${item.approved ? "btn-outline" : "btn-primary"}`}
            >
              {working === "approve" ? <Loader2 className="animate-spin" size={14}/> : <Check size={14}/>}
              {item.approved ? "Retirer approbation" : "Approuver"}
            </button>
            <button
              onClick={regenImage}
              disabled={working !== ""}
              data-testid={`kit-regen-image-${item.id}`}
              className="btn btn-outline text-xs"
            >
              {working === "image" ? <Loader2 className="animate-spin" size={14}/> : <Sparkles size={14}/>}
              Image IA
            </button>
            <button
              onClick={regenText}
              disabled={working !== ""}
              data-testid={`kit-regen-text-${item.id}`}
              className="btn btn-outline text-xs"
            >
              {working === "text" ? <Loader2 className="animate-spin" size={14}/> : <RefreshCw size={14}/>}
              Texte
            </button>
            <label className="btn btn-outline text-xs cursor-pointer">
              {working === "replace" ? <Loader2 className="animate-spin" size={14}/> : <Upload size={14}/>}
              Remplacer
              <input
                type="file" accept="image/*" hidden
                onChange={(e) => replaceImage(e.target.files?.[0])}
                data-testid={`kit-replace-${item.id}`}
              />
            </label>
            <button
              onClick={copyAll}
              data-testid={`kit-copy-${item.id}`}
              className="btn btn-outline text-xs"
            >
              <Copy size={14}/> Copier
            </button>
            <label className="ml-auto flex items-center gap-2 text-xs text-text-cream/70 cursor-pointer select-none px-2">
              <input
                type="checkbox"
                checked={item.published}
                onChange={togglePublished}
                data-testid={`kit-published-${item.id}`}
              />
              Publié
            </label>
          </div>
        </div>
      </div>
    </div>
  );
}

// ============================================================
// KIT ACCORDION
// ============================================================
function KitAccordion({ kit, defaultOpen, onReload }) {
  const [open, setOpen] = React.useState(defaultOpen);

  const patch = async (itemId, body) => {
    const r = await api.patch(`/admin/marketing/kits/${kit.id}/items/${itemId}`, body);
    if (onReload) await onReload();
    return r.data;
  };

  const regenText = async (itemId) => {
    await api.post(`/admin/marketing/kits/${kit.id}/items/${itemId}/regen-text`);
    if (onReload) await onReload();
  };

  const regenImage = async (itemId) => {
    await api.post(`/admin/marketing/kits/${kit.id}/items/${itemId}/regen-image`);
    if (onReload) await onReload();
  };

  const downloadZip = async () => {
    const token = localStorage.getItem("se_admin_token");
    const r = await fetch(`${BACKEND}/api/admin/marketing/kits/${kit.id}/download`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!r.ok) { alert("Échec téléchargement"); return; }
    const blob = await r.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `kit-marketing-${kit.week_start}.zip`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const removeKit = async () => {
    if (!confirm(`Supprimer le kit de la semaine ${kit.week_label} ?`)) return;
    await api.delete(`/admin/marketing/kits/${kit.id}`);
    if (onReload) await onReload();
  };

  const total = (kit.posts?.length || 0) + (kit.stories?.length || 0);
  const approved = [...(kit.posts || []), ...(kit.stories || [])].filter(x => x.approved).length;

  return (
    <div className="rounded-xl border border-white/10 bg-black/20" data-testid={`kit-accordion-${kit.id}`}>
      <div
        className="flex items-center gap-3 p-4 cursor-pointer select-none flex-wrap"
        onClick={() => setOpen(o => !o)}
      >
        {open ? <ChevronUp size={18}/> : <ChevronDown size={18}/>}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="font-serif text-xl">Semaine du {kit.week_label}</h3>
            {kit.status === "approved"
              ? <Badge tone="ok">Approuvé ({approved}/{total})</Badge>
              : <Badge tone="warn">À valider ({approved}/{total})</Badge>}
            <span className="text-xs text-text-cream/40">
              · {kit.ai_images_used} images IA · {kit.posts?.length || 0} posts · {kit.stories?.length || 0} stories
            </span>
          </div>
        </div>
        <button
          onClick={(e) => { e.stopPropagation(); downloadZip(); }}
          data-testid={`kit-download-${kit.id}`}
          className="btn btn-outline text-xs"
        >
          <FileArchive size={14}/> Tout télécharger (.zip)
        </button>
        <button
          onClick={(e) => { e.stopPropagation(); removeKit(); }}
          data-testid={`kit-remove-${kit.id}`}
          className="btn btn-outline text-xs opacity-60 hover:opacity-100 hover:text-red-300"
        >
          <Trash2 size={14}/>
        </button>
      </div>

      {open && (
        <div className="p-4 pt-0 flex flex-col gap-4 border-t border-white/5">
          <h4 className="eyebrow text-text-cream/50 mt-4">Posts (3)</h4>
          <div className="flex flex-col gap-4">
            {(kit.posts || []).map(it => (
              <KitItemCard
                key={it.id}
                item={it}
                kind="post"
                onPatch={patch}
                onRegenText={regenText}
                onRegenImage={regenImage}
              />
            ))}
          </div>
          <h4 className="eyebrow text-text-cream/50 mt-6">Stories (5)</h4>
          <div className="flex flex-col gap-4">
            {(kit.stories || []).map(it => (
              <KitItemCard
                key={it.id}
                item={it}
                kind="story"
                onPatch={patch}
                onRegenText={regenText}
                onRegenImage={regenImage}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ============================================================
// KITS TAB
// ============================================================
function KitsTab() {
  const [kits, setKits] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [generating, setGenerating] = React.useState(false);
  const [maxImages, setMaxImages] = React.useState(15);
  const [err, setErr] = React.useState("");

  const load = React.useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/marketing/kits");
      setKits(r.data.items || []);
    } catch { /* ignore */ } finally { setLoading(false); }
  }, []);

  React.useEffect(() => { load(); }, [load]);

  const generate = async () => {
    setGenerating(true); setErr("");
    try {
      await api.post("/admin/marketing/kits/generate", { max_images: maxImages });
      await load();
    } catch (e) {
      setErr(e?.response?.data?.detail || "Échec génération");
    } finally { setGenerating(false); }
  };

  const totalAi = kits.reduce((s, k) => s + (k.ai_images_used || 0), 0);
  const lastAi = kits[0]?.ai_images_used || 0;

  return (
    <div data-testid="marketing-kits-tab">
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <h3 className="font-serif text-2xl">Contenu Instagram de la semaine</h3>
          <p className="text-sm text-text-cream/50 mt-1 max-w-2xl font-light">
            {lastAi} / {maxImages} images IA (dernier pack).
            Généré chaque dimanche à 18 h 00 (Europe/Zurich) : 3 posts (lun/mer/ven) + 5 stories.
            Photos réelles si marquées <em>Utilisable Instagram</em> et correspondant à la catégorie.
            Publication manuelle : téléchargez le .zip et postez depuis Instagram.
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <label className="flex items-center gap-2 text-xs text-text-cream/60">
            Max / semaine
            <input
              type="number" min={0} max={30}
              value={maxImages}
              onChange={(e) => setMaxImages(Number(e.target.value || 0))}
              data-testid="kit-max-images"
              className="w-16 bg-black/40 border border-white/10 rounded px-2 py-1.5 text-sm"
            />
          </label>
          <button
            onClick={generate}
            disabled={generating}
            data-testid="kit-generate-btn"
            className="btn btn-primary"
          >
            {generating ? <Loader2 className="animate-spin" size={16}/> : <Sparkles size={16}/>}
            {generating ? "Génération en cours (~1 min)…" : "Générer maintenant"}
          </button>
        </div>
      </div>

      {err && (
        <div className="mt-3 p-3 rounded-lg bg-red-500/10 border border-red-500/30 text-red-200 text-sm flex items-center gap-2">
          <AlertCircle size={16}/> {err}
        </div>
      )}

      {generating && (
        <div className="mt-4 p-4 rounded-lg bg-sky-500/10 border border-sky-500/30 text-sky-100 text-sm">
          Nous générons les 8 visuels IA et les légendes avec Gemini 3.1 Pro.
          Cela prend environ 1 minute. Vous pouvez continuer à travailler, l'écran se mettra à jour.
        </div>
      )}

      <div className="mt-8 flex flex-col gap-4">
        {loading && <div className="text-text-cream/50 text-sm">Chargement…</div>}
        {!loading && kits.length === 0 && (
          <div className="p-10 text-center rounded-xl border border-dashed border-white/10 text-text-cream/50">
            Aucun kit encore. Cliquez sur <strong>Générer maintenant</strong> pour créer le premier.
          </div>
        )}
        {kits.map((k, i) => (
          <KitAccordion key={k.id} kit={k} defaultOpen={i === 0} onReload={load}/>
        ))}
      </div>
    </div>
  );
}

// ============================================================
// CAROUSELS TAB
// ============================================================
const FONT_OPTIONS = [
  { key: "serif", label: "Serif élégant (Playfair)" },
  { key: "script", label: "Script manuscrit (Dancing)" },
  { key: "sans", label: "Sans-serif moderne (Montserrat)" },
  { key: "hand", label: "Note manuscrite (Caveat)" },
];

const DEFAULT_SLIDE_STYLE = {
  title_font: "serif",
  title_color: "#FFFFFF",
  title_size: 82,
  title_bg_color: "#0F0F14CC",
  title_bg_enabled: true,
  subtitle_font: "sans",
  subtitle_color: "#F5F0E6",
  subtitle_size: 28,
  bullets_font: "sans",
  bullets_color: "#F5F0E6",
  bullets_size: 30,
  check_color: "#E6C080",
  annotation_font: "hand",
  annotation_color: "#E6C080",
  annotation_size: 44,
  overlay_strength: 55,
  text_align: "left",
  accent_color: "#E6C080",
  header_pill: true,
};

function hexFromRgba(rgba) {
  // Not used — we work with #RRGGBB or #RRGGBBAA strings directly
  return rgba;
}

function StyleEditor({ style, onChange }) {
  const s = { ...DEFAULT_SLIDE_STYLE, ...(style || {}) };
  const set = (k, v) => onChange({ ...s, [k]: v });

  // #RRGGBBAA → split color (#RRGGBB) + alpha (0-255)
  const splitHex8 = (hex) => {
    const clean = (hex || "#000000FF").replace("#", "");
    if (clean.length === 6) return { color: `#${clean}`, alpha: 255 };
    if (clean.length === 8) return { color: `#${clean.slice(0, 6)}`, alpha: parseInt(clean.slice(6, 8), 16) };
    return { color: "#000000", alpha: 255 };
  };
  const joinHex8 = (color, alpha) => `${color}${alpha.toString(16).padStart(2, "0").toUpperCase()}`;
  const titleBg = splitHex8(s.title_bg_color);

  const Row = ({ label, children }) => (
    <div className="grid grid-cols-[140px_1fr] items-center gap-3 py-1.5">
      <label className="text-xs text-text-cream/60">{label}</label>
      <div className="flex items-center gap-2 flex-wrap">{children}</div>
    </div>
  );

  const Color = ({ value, onChange: oc, testid }) => (
    <input
      type="color"
      value={value}
      onChange={(e) => oc(e.target.value.toUpperCase())}
      data-testid={testid}
      className="w-10 h-8 rounded border border-white/10 bg-transparent cursor-pointer"
    />
  );

  const Num = ({ value, onChange: oc, min, max, testid }) => (
    <input
      type="number"
      value={value}
      min={min}
      max={max}
      onChange={(e) => oc(Math.max(min, Math.min(max, parseInt(e.target.value || "0", 10))))}
      data-testid={testid}
      className="w-20 bg-black/40 border border-white/10 rounded px-2 py-1 text-xs"
    />
  );

  const Select = ({ value, onChange: oc, options, testid }) => (
    <select
      value={value}
      onChange={(e) => oc(e.target.value)}
      data-testid={testid}
      className="bg-black/40 border border-white/10 rounded px-2 py-1.5 text-xs flex-1 min-w-[140px]"
    >
      {options.map(o => <option key={o.key} value={o.key}>{o.label}</option>)}
    </select>
  );

  return (
    <div className="rounded-lg bg-black/30 border border-white/5 p-4 flex flex-col gap-1 text-xs">
      <div className="flex items-center justify-between mb-2">
        <h5 className="font-serif text-sm text-text-cream/80">Style de la slide</h5>
        <button
          onClick={() => onChange({ ...DEFAULT_SLIDE_STYLE })}
          className="text-xs text-text-cream/50 hover:text-text-cream/80 underline"
          data-testid="style-reset"
        >
          Réinitialiser
        </button>
      </div>

      <Row label="Titre — police">
        <Select value={s.title_font} onChange={v => set("title_font", v)} options={FONT_OPTIONS} testid="style-title-font"/>
        <Num value={s.title_size} onChange={v => set("title_size", v)} min={30} max={160} testid="style-title-size"/>
        <Color value={s.title_color} onChange={v => set("title_color", v)} testid="style-title-color"/>
      </Row>

      <Row label="Fond du titre">
        <label className="flex items-center gap-1 text-text-cream/70">
          <input type="checkbox" checked={s.title_bg_enabled} onChange={e => set("title_bg_enabled", e.target.checked)} data-testid="style-title-bg-enabled"/>
          Activé
        </label>
        <Color value={titleBg.color} onChange={v => set("title_bg_color", joinHex8(v, titleBg.alpha))} testid="style-title-bg-color"/>
        <label className="text-text-cream/50 ml-2">Opacité fond</label>
        <input
          type="range" min={0} max={255} value={titleBg.alpha}
          onChange={e => set("title_bg_color", joinHex8(titleBg.color, parseInt(e.target.value, 10)))}
          data-testid="style-title-bg-alpha"
          className="w-24"
        />
      </Row>

      <Row label="Sous-titre">
        <Select value={s.subtitle_font} onChange={v => set("subtitle_font", v)} options={FONT_OPTIONS} testid="style-sub-font"/>
        <Num value={s.subtitle_size} onChange={v => set("subtitle_size", v)} min={14} max={60} testid="style-sub-size"/>
        <Color value={s.subtitle_color} onChange={v => set("subtitle_color", v)} testid="style-sub-color"/>
      </Row>

      <Row label="Bullets">
        <Select value={s.bullets_font} onChange={v => set("bullets_font", v)} options={FONT_OPTIONS} testid="style-bul-font"/>
        <Num value={s.bullets_size} onChange={v => set("bullets_size", v)} min={14} max={60} testid="style-bul-size"/>
        <Color value={s.bullets_color} onChange={v => set("bullets_color", v)} testid="style-bul-color"/>
        <label className="text-text-cream/50 ml-2">Cochet</label>
        <Color value={s.check_color} onChange={v => set("check_color", v)} testid="style-check-color"/>
      </Row>

      <Row label="Annotation">
        <Select value={s.annotation_font} onChange={v => set("annotation_font", v)} options={FONT_OPTIONS} testid="style-ann-font"/>
        <Num value={s.annotation_size} onChange={v => set("annotation_size", v)} min={20} max={80} testid="style-ann-size"/>
        <Color value={s.annotation_color} onChange={v => set("annotation_color", v)} testid="style-ann-color"/>
      </Row>

      <Row label="Voile photo">
        <input
          type="range" min={0} max={100} value={s.overlay_strength}
          onChange={e => set("overlay_strength", parseInt(e.target.value, 10))}
          data-testid="style-overlay"
          className="w-full max-w-[200px]"
        />
        <span className="text-text-cream/60 w-10">{s.overlay_strength}%</span>
      </Row>

      <Row label="Alignement">
        <div className="flex gap-1">
          {["left", "center", "right"].map(a => (
            <button
              key={a}
              onClick={() => set("text_align", a)}
              data-testid={`style-align-${a}`}
              className={`px-3 py-1 rounded text-xs border transition ${
                s.text_align === a
                  ? "bg-accent-deep/30 text-accent-light border-accent-deep/60"
                  : "border-white/15 text-text-cream/60"
              }`}
            >{a === "left" ? "Gauche" : a === "center" ? "Centre" : "Droite"}</button>
          ))}
        </div>
      </Row>

      <Row label="Accent (brand)">
        <Color value={s.accent_color} onChange={v => set("accent_color", v)} testid="style-accent-color"/>
      </Row>
    </div>
  );
}

function CarouselSlideCard({ slide, total, onUpdate, mediaByCategory, onApplyStyleAll, layouts = [], onMoveUp, onMoveDown, onExport }) {
  const [draft, setDraft] = React.useState({
    title: slide.title,
    subtitle: slide.subtitle,
    bullets: (slide.bullets || []).join("\n"),
    annotation: slide.annotation,
    style: slide.style || {},
  });
  const [working, setWorking] = React.useState("");
  const [showPicker, setShowPicker] = React.useState(false);
  const [showStyle, setShowStyle] = React.useState(false);

  React.useEffect(() => {
    setDraft({
      title: slide.title,
      subtitle: slide.subtitle,
      bullets: (slide.bullets || []).join("\n"),
      annotation: slide.annotation,
      style: slide.style || {},
    });
  }, [slide.id, slide.title, slide.subtitle, slide.bullets, slide.annotation, slide.style]);

  const save = async () => {
    setWorking("save");
    try {
      await onUpdate(slide.id, {
        title: draft.title,
        subtitle: draft.subtitle,
        bullets: draft.bullets.split("\n").map(s => s.trim()).filter(Boolean),
        annotation: draft.annotation,
        style: draft.style,
      });
    } finally { setWorking(""); }
  };

  const changePhoto = async (url) => {
    setWorking("photo");
    setShowPicker(false);
    try { await onUpdate(slide.id, { source_image_url: url }); } finally { setWorking(""); }
  };

  const applyStyleToAll = async () => {
    if (!onApplyStyleAll) return;
    setWorking("apply-all");
    try { await onApplyStyleAll(draft.style); } finally { setWorking(""); }
  };

  const allPhotos = Object.values(mediaByCategory || {}).flat();

  return (
    <div data-testid={`slide-${slide.id}`} className="rounded-xl border border-white/10 bg-black/20 overflow-hidden">
      <div className="grid grid-cols-1 md:grid-cols-[260px_1fr] gap-0">
        <div className="relative bg-black/40 aspect-[4/5]">
          {slide.rendered_url ? (
            <img src={absUrl(slide.rendered_url)} alt="" className="w-full h-full object-cover"/>
          ) : (
            <div className="w-full h-full flex items-center justify-center text-text-cream/40 text-xs">
              Rendu en attente
            </div>
          )}
          <div className="absolute top-2 left-2">
            <Badge tone="default">
              {slide.kind === "cover" ? "Couverture" : slide.kind === "summary" ? "Résumé" : `Slide ${slide.index + 1}/${total}`}
            </Badge>
          </div>
        </div>

        <div className="p-4 flex flex-col gap-3">
          <input
            type="text"
            value={draft.title}
            onChange={(e) => setDraft(s => ({ ...s, title: e.target.value }))}
            placeholder="Titre"
            data-testid={`slide-title-${slide.id}`}
            className="bg-black/40 border border-white/10 rounded px-3 py-2 text-sm font-serif"
          />
          <input
            type="text"
            value={draft.subtitle}
            onChange={(e) => setDraft(s => ({ ...s, subtitle: e.target.value }))}
            placeholder="Sous-titre"
            data-testid={`slide-subtitle-${slide.id}`}
            className="bg-black/40 border border-white/10 rounded px-3 py-2 text-sm font-light"
          />
          {slide.kind !== "cover" && (
            <textarea
              value={draft.bullets}
              onChange={(e) => setDraft(s => ({ ...s, bullets: e.target.value }))}
              rows={4}
              placeholder="Un bullet par ligne"
              data-testid={`slide-bullets-${slide.id}`}
              className="bg-black/40 border border-white/10 rounded px-3 py-2 text-sm font-light resize-none"
            />
          )}
          <input
            type="text"
            value={draft.annotation}
            onChange={(e) => setDraft(s => ({ ...s, annotation: e.target.value }))}
            placeholder="Annotation manuscrite (en bas)"
            data-testid={`slide-annotation-${slide.id}`}
            className="bg-black/40 border border-white/10 rounded px-3 py-2 text-sm font-light italic"
          />

          <button
            onClick={() => setShowStyle(v => !v)}
            data-testid={`slide-toggle-style-${slide.id}`}
            className="btn btn-outline text-xs self-start"
          >
            <Pencil size={14}/> {showStyle ? "Masquer" : "Modifier"} le style
          </button>

          {showStyle && (
            <>
              <StyleEditor
                style={draft.style}
                onChange={(newStyle) => setDraft(s => ({ ...s, style: newStyle }))}
              />
              {onApplyStyleAll && (
                <button
                  onClick={applyStyleToAll}
                  disabled={working !== ""}
                  data-testid={`slide-apply-style-all-${slide.id}`}
                  className="btn btn-outline text-xs self-start"
                >
                  {working === "apply-all" ? <Loader2 className="animate-spin" size={14}/> : <Sparkles size={14}/>}
                  Appliquer ce style à toutes les slides
                </button>
              )}
            </>
          )}

          <div className="flex flex-wrap gap-2 pt-2 border-t border-white/5">
            <button
              onClick={save}
              disabled={working !== ""}
              data-testid={`slide-save-${slide.id}`}
              className="btn btn-primary text-xs"
            >
              {working === "save" ? <Loader2 className="animate-spin" size={14}/> : <RefreshCw size={14}/>}
              Enregistrer & régénérer
            </button>
            {layouts.length > 0 && (
              <select
                value={slide.layout}
                onChange={async (e) => {
                  setWorking("layout");
                  try { await onUpdate(slide.id, { layout: e.target.value }); } finally { setWorking(""); }
                }}
                data-testid={`slide-layout-${slide.id}`}
                className="bg-black/40 border border-white/10 rounded px-2 py-1.5 text-xs"
              >
                {layouts.map(l => <option key={l.key} value={l.key}>{l.label}</option>)}
              </select>
            )}
            <button
              onClick={() => setShowPicker(v => !v)}
              data-testid={`slide-pick-photo-${slide.id}`}
              className="btn btn-outline text-xs"
            >
              <ImageIcon size={14}/> Photo
            </button>
            {onMoveUp && (
              <button onClick={onMoveUp} className="btn btn-outline text-xs" data-testid={`slide-up-${slide.id}`}>
                <ChevronUp size={14}/>
              </button>
            )}
            {onMoveDown && (
              <button onClick={onMoveDown} className="btn btn-outline text-xs" data-testid={`slide-down-${slide.id}`}>
                <ChevronDown size={14}/>
              </button>
            )}
            {onExport && (
              <>
                <button onClick={() => onExport(slide.id, "jpg")} className="btn btn-outline text-xs" data-testid={`slide-export-jpg-${slide.id}`}>
                  <Download size={14}/> JPG
                </button>
                <button onClick={() => onExport(slide.id, "png")} className="btn btn-outline text-xs" data-testid={`slide-export-png-${slide.id}`}>
                  <Download size={14}/> PNG
                </button>
              </>
            )}
          </div>

          {showPicker && (
            <div className="mt-2 p-3 rounded-lg bg-black/40 border border-white/10 max-h-64 overflow-y-auto">
              <div className="text-xs text-text-cream/60 mb-2">
                Choisissez une photo ({allPhotos.length} disponible{allPhotos.length > 1 ? "s" : ""})
              </div>
              <div className="grid grid-cols-5 md:grid-cols-7 gap-1.5">
                {allPhotos.map((p) => (
                  <button
                    key={p.id}
                    onClick={() => changePhoto(p.url)}
                    data-testid={`slide-pick-${p.id}`}
                    className="aspect-square rounded overflow-hidden border border-white/10 hover:border-accent-deep transition"
                  >
                    <img src={absUrl(p.url)} alt="" className="w-full h-full object-cover"/>
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function CarouselCard({ carousel, mediaByCategory, onReload, layouts = [], formats = [] }) {
  const [open, setOpen] = React.useState(true);

  const update = async (slideId, body) => {
    await api.patch(`/admin/marketing/carousels/${carousel.id}/slides/${slideId}`, body);
    if (onReload) await onReload();
  };

  const applyStyleToAll = async (styleObj) => {
    for (const s of (carousel.slides || [])) {
      await api.patch(`/admin/marketing/carousels/${carousel.id}/slides/${s.id}`, { style: styleObj });
    }
    if (onReload) await onReload();
  };

  const changeFormat = async (newFormat) => {
    if (!confirm(`Changer le format vers ${newFormat} ? Toutes les slides vont être recomposées.`)) return;
    await api.patch(`/admin/marketing/carousels/${carousel.id}`, { format: newFormat });
    if (onReload) await onReload();
  };

  const reorderSlide = async (slideId, direction) => {
    const ids = (carousel.slides || []).map(s => s.id);
    const idx = ids.indexOf(slideId);
    const newIdx = direction === "up" ? idx - 1 : idx + 1;
    if (newIdx < 0 || newIdx >= ids.length) return;
    [ids[idx], ids[newIdx]] = [ids[newIdx], ids[idx]];
    await api.post(`/admin/marketing/carousels/${carousel.id}/reorder`, { slide_ids: ids });
    if (onReload) await onReload();
  };

  const exportSlide = async (slideId, fmt = "jpg") => {
    const token = localStorage.getItem("se_admin_token");
    const r = await fetch(`${BACKEND}/api/admin/marketing/carousels/${carousel.id}/slides/${slideId}/export?fmt=${fmt}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!r.ok) { alert("Échec export"); return; }
    const blob = await r.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `slide.${fmt}`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const rerender = async () => {
    await api.post(`/admin/marketing/carousels/${carousel.id}/rerender`);
    if (onReload) await onReload();
  };

  const download = async () => {
    const token = localStorage.getItem("se_admin_token");
    const r = await fetch(`${BACKEND}/api/admin/marketing/carousels/${carousel.id}/download`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!r.ok) { alert("Échec téléchargement"); return; }
    const blob = await r.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `carrousel-${carousel.theme_key}.zip`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const remove = async () => {
    if (!confirm("Supprimer ce carrousel ?")) return;
    await api.delete(`/admin/marketing/carousels/${carousel.id}`);
    if (onReload) await onReload();
  };

  const total = carousel.slides?.length || 0;

  return (
    <div className="rounded-xl border border-white/10 bg-black/20" data-testid={`carousel-card-${carousel.id}`}>
      <div className="p-4 flex items-center gap-3 flex-wrap cursor-pointer" onClick={() => setOpen(o => !o)}>
        {open ? <ChevronUp size={18}/> : <ChevronDown size={18}/>}
        <div className="flex-1 min-w-0">
          <h3 className="font-serif text-xl truncate">{carousel.theme_label}</h3>
          <p className="text-xs text-text-cream/50 mt-0.5 truncate">{carousel.subject}</p>
        </div>
        <Badge>{total} slides · {carousel.format || "post_4x5"}</Badge>
        {formats.length > 0 && (
          <select
            value={carousel.format || "post_4x5"}
            onClick={(e) => e.stopPropagation()}
            onChange={(e) => { e.stopPropagation(); changeFormat(e.target.value); }}
            data-testid={`carousel-format-${carousel.id}`}
            className="bg-black/40 border border-white/10 rounded px-2 py-1.5 text-xs"
          >
            {formats.map(f => <option key={f.key} value={f.key}>{f.key.replace("_", " ")}</option>)}
          </select>
        )}
        <button
          onClick={(e) => { e.stopPropagation(); rerender(); }}
          data-testid={`carousel-rerender-${carousel.id}`}
          className="btn btn-outline text-xs"
          title="Re-rendre toutes les slides"
        >
          <RefreshCw size={14}/> Re-rendre
        </button>
        <button
          onClick={(e) => { e.stopPropagation(); download(); }}
          data-testid={`carousel-download-${carousel.id}`}
          className="btn btn-outline text-xs"
        >
          <FileArchive size={14}/> .zip
        </button>
        <button
          onClick={(e) => { e.stopPropagation(); remove(); }}
          data-testid={`carousel-remove-${carousel.id}`}
          className="btn btn-outline text-xs opacity-60 hover:opacity-100 hover:text-red-300"
        >
          <Trash2 size={14}/>
        </button>
      </div>
      {open && (
        <div className="p-4 pt-0 border-t border-white/5 flex flex-col gap-4">
          {(carousel.slides || []).map((s, idx) => (
            <CarouselSlideCard
              key={s.id}
              slide={s}
              total={total}
              onUpdate={update}
              mediaByCategory={mediaByCategory}
              onApplyStyleAll={applyStyleToAll}
              layouts={layouts}
              onMoveUp={idx > 0 ? () => reorderSlide(s.id, "up") : null}
              onMoveDown={idx < total - 1 ? () => reorderSlide(s.id, "down") : null}
              onExport={exportSlide}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function CarouselsTab() {
  const [carousels, setCarousels] = React.useState([]);
  const [themes, setThemes] = React.useState([]);
  const [layouts, setLayouts] = React.useState([]);
  const [formats, setFormats] = React.useState([]);
  const [typographyDefaults, setTypographyDefaults] = React.useState({});
  const [media, setMedia] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [generating, setGenerating] = React.useState(false);
  const [mode, setMode] = React.useState("theme");
  const [themeKey, setThemeKey] = React.useState("");
  const [customSubject, setCustomSubject] = React.useState("");
  const [slideCount, setSlideCount] = React.useState("");
  const [format, setFormat] = React.useState("post_4x5");
  const [err, setErr] = React.useState("");

  const load = React.useCallback(async () => {
    setLoading(true);
    try {
      const [c, t, m] = await Promise.all([
        api.get("/admin/marketing/carousels"),
        api.get("/admin/marketing/carousels/themes"),
        api.get("/admin/marketing/media"),
      ]);
      setCarousels(c.data.items || []);
      setThemes(t.data.themes || []);
      setLayouts(t.data.layouts || []);
      setFormats(t.data.formats || []);
      setTypographyDefaults(t.data.typography_defaults || {});
      setMedia(m.data.items || []);
      if (!themeKey && t.data.themes?.length) setThemeKey(t.data.themes[0].key);
    } catch { setErr("Chargement des carrousels impossible. Réessayez dans un instant."); } finally { setLoading(false); }
  }, [themeKey]);

  React.useEffect(() => { load(); }, []); // eslint-disable-line

  const mediaByCategory = React.useMemo(() => {
    const by = {};
    for (const m of media) {
      if (!m.usable_for_marketing) continue;
      (by[m.category] = by[m.category] || []).push(m);
    }
    return by;
  }, [media]);

  const generate = async () => {
    setGenerating(true); setErr("");
    try {
      const payload = {};
      if (mode === "theme") payload.theme_key = themeKey;
      else payload.subject = customSubject.trim();
      if (slideCount) payload.slide_count = parseInt(slideCount, 10);
      if (format) payload.format = format;
      if (mode === "custom" && !payload.subject) {
        setErr("Veuillez écrire un sujet");
        setGenerating(false);
        return;
      }
      await api.post("/admin/marketing/carousels", payload);
      await load();
    } catch (e) {
      setErr(e?.response?.data?.detail || "Échec génération");
    } finally { setGenerating(false); }
  };

  const totalPhotos = Object.values(mediaByCategory).flat().length;

  return (
    <div data-testid="carousels-tab">
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <h3 className="font-serif text-2xl">Carrousels pédagogiques</h3>
          <p className="text-sm text-text-cream/50 mt-1 max-w-2xl font-light">
            Générez un carrousel Instagram multi-slides (3 à 6 pages) autour d'une astuce photo.
            L'IA rédige les textes, le système compose les slides avec vos photos studio en fond.
            {totalPhotos === 0 && <> <strong className="text-amber-200">Astuce : ajoutez des photos dans la bibliothèque</strong> (onglet Bibliothèque) pour qu'elles apparaissent en fond des slides.</>}
          </p>
        </div>
      </div>

      {/* Generation controls */}
      <div className="mt-6 p-5 rounded-xl border border-white/10 bg-black/20">
        <div className="flex gap-2 mb-4">
          <button
            onClick={() => setMode("theme")}
            data-testid="carousel-mode-theme"
            className={`px-4 py-2 rounded-lg text-xs transition border ${
              mode === "theme" ? "bg-accent-deep/20 text-accent-light border-accent-deep/50" : "border-white/15 text-text-cream/70"
            }`}
          >Thème prédéfini</button>
          <button
            onClick={() => setMode("custom")}
            data-testid="carousel-mode-custom"
            className={`px-4 py-2 rounded-lg text-xs transition border ${
              mode === "custom" ? "bg-accent-deep/20 text-accent-light border-accent-deep/50" : "border-white/15 text-text-cream/70"
            }`}
          >Sujet personnalisé</button>
        </div>

        {mode === "theme" ? (
          <select
            value={themeKey}
            onChange={(e) => setThemeKey(e.target.value)}
            data-testid="carousel-theme-select"
            className="w-full bg-black/40 border border-white/10 rounded px-3 py-2.5 text-sm"
          >
            {themes.map(t => <option key={t.key} value={t.key}>{t.label}</option>)}
          </select>
        ) : (
          <input
            type="text"
            value={customSubject}
            onChange={(e) => setCustomSubject(e.target.value)}
            placeholder="Ex: Comment choisir sa focale pour un portrait maternité"
            data-testid="carousel-custom-input"
            className="w-full bg-black/40 border border-white/10 rounded px-3 py-2.5 text-sm"
          />
        )}

        <div className="mt-4 flex items-center gap-3 flex-wrap">
          <label className="text-xs text-text-cream/60 flex items-center gap-2">
            Format
            <select
              value={format}
              onChange={(e) => setFormat(e.target.value)}
              data-testid="carousel-format"
              className="bg-black/40 border border-white/10 rounded px-2 py-1.5 text-xs"
            >
              {formats.map(f => (
                <option key={f.key} value={f.key}>
                  {f.key === "post_4x5" ? "Post 4:5 (1080×1350)"
                    : f.key === "square_1x1" ? "Carré 1:1 (1080×1080)"
                    : f.key === "story_9x16" ? "Story/Reel 9:16 (1080×1920)"
                    : f.key}
                </option>
              ))}
            </select>
          </label>
          <label className="text-xs text-text-cream/60 flex items-center gap-2">
            Nombre de slides
            <select
              value={slideCount}
              onChange={(e) => setSlideCount(e.target.value)}
              data-testid="carousel-slide-count"
              className="bg-black/40 border border-white/10 rounded px-2 py-1.5 text-xs"
            >
              <option value="">Auto (IA décide)</option>
              <option value="3">3</option>
              <option value="4">4</option>
              <option value="5">5</option>
              <option value="6">6</option>
            </select>
          </label>
          <button
            onClick={generate}
            disabled={generating}
            data-testid="carousel-generate-btn"
            className="btn btn-primary ml-auto"
          >
            {generating ? <Loader2 className="animate-spin" size={16}/> : <Sparkles size={16}/>}
            {generating ? "Génération en cours (~30s)…" : "Créer le carrousel"}
          </button>
        </div>

        {err && (
          <div className="mt-3 p-3 rounded-lg bg-red-500/10 border border-red-500/30 text-red-200 text-sm flex items-center gap-2">
            <AlertCircle size={16}/> {err}
          </div>
        )}
      </div>

      {/* List */}
      <div className="mt-8 flex flex-col gap-4">
        {loading && <div className="text-text-cream/50 text-sm">Chargement…</div>}
        {!loading && carousels.length === 0 && (
          <div className="p-10 text-center rounded-xl border border-dashed border-white/10 text-text-cream/50">
            Aucun carrousel pour l'instant. Choisissez un thème et cliquez sur <strong>Créer le carrousel</strong>.
          </div>
        )}
        {carousels.map(c => (
          <CarouselEditor key={c.id} carousel={c} mediaByCategory={mediaByCategory} onReload={load} layouts={layouts} formats={formats} typographyDefaults={typographyDefaults}/>
        ))}
      </div>
    </div>
  );
}

// ============================================================
// MAIN
// ============================================================
export default function AdminMarketing() {
  const [tab, setTab] = React.useState(() => sessionStorage.getItem("marketing-active-tab") || "planner");

  return (
    <div className="space-y-8" data-testid="admin-marketing-page">
      <div>
        <div className="eyebrow text-text-cream/40">Marketing</div>
        <h1 className="font-serif text-4xl mt-1">Kit Instagram hebdomadaire</h1>
      </div>

      <div className="flex gap-2 border-b border-white/10 flex-wrap">
        {[
          ["planner", "Créateur de contenu IA"],
          ["kits", "Kits hebdo"],
          ["carousels", "Carrousels pédagogiques"],
          ["media", "Bibliothèque photos"],
        ].map(([k, l]) => (
          <button
            key={k}
            onClick={() => { setTab(k); sessionStorage.setItem("marketing-active-tab", k); }}
            data-testid={`marketing-tab-${k}`}
            className={`px-5 py-3 text-sm transition border-b-2 -mb-px ${
              tab === k
                ? "text-accent-light border-accent-deep"
                : "text-text-cream/60 hover:text-text-cream border-transparent"
            }`}
          >
            {l}
          </button>
        ))}
      </div>

      {tab === "planner" && <ContentPlanner/>}
      {tab === "kits" && <KitsTab/>}
      {tab === "carousels" && <CarouselsTab/>}
      {tab === "media" && <MediaLibrary/>}
    </div>
  );
}
